import { deleteAPI, getAPI, patchAPI, postAPI, postFileAPI } from "./Networking";
import { SwayyAPIResponse } from "../../../models/NetworkResponse";
import {
  CreateSwayyShipmentPayload,
  GetSwayyShipmentsParams,
  SwayyCourierPartner,
  SwayyShipment,
  SwayyShipmentFullDetail,
  SwayyShipmentsListData,
  UpdateSwayyShipmentPayload,
} from "../../../models/SwayyShipment/SwayyShipment";

const getErrorMessage = (error: unknown, fallback: string): string => {
  const body = (error as any)?.response?.data;
  const details = body?.data?.details;
  if (Array.isArray(details) && details.length > 0) {
    const lines = details
      .map((detail: { path?: string; message?: string }) => {
        if (detail?.path && detail?.message) {
          return `${detail.path}: ${detail.message}`;
        }
        return detail?.message || "";
      })
      .filter(Boolean);
    if (lines.length > 0) return lines.join("\n");
  }
  if (typeof body?.message === "string" && body.message.trim()) {
    return body.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

export default class SwayyShipmentAPI {
  getQueryParams = (
    params: GetSwayyShipmentsParams,
  ): Record<string, string | number | boolean> => {
    const query: Record<string, string | number | boolean> = {
      page: params.page,
      limit: params.limit,
    };
    if (params.status) query.status = params.status;
    if (params.carrier_id) query.carrier_id = params.carrier_id;
    if (params.warehouse_id) query.warehouse_id = params.warehouse_id;
    if (params.awb) query.awb = params.awb;
    if (params.from_date) query.from_date = params.from_date;
    if (params.to_date) query.to_date = params.to_date;
    if (params.overdue === true) query.overdue = "true";
    if (params.overdue === false) query.overdue = "false";
    return query;
  };

  getShipments = async (
    params: GetSwayyShipmentsParams,
  ): Promise<SwayyShipmentsListData> => {
    try {
      const response = (await getAPI(
        "/v1/shipments/admin",
        this.getQueryParams(params),
      )) as SwayyAPIResponse<SwayyShipmentsListData>;

      const payload = response.data ?? response.result;
      if (!response.success || !payload) {
        throw new Error(response.message || "Failed to fetch shipments");
      }

      return {
        shipments: payload.shipments ?? [],
        pagination: {
          total: payload.pagination?.total ?? 0,
          page: payload.pagination?.page ?? params.page,
          limit: payload.pagination?.limit ?? params.limit,
        },
      };
    } catch (error) {
      throw error;
    }
  };

  getShipment = async (
    shipmentId: number | string,
  ): Promise<SwayyShipmentFullDetail> => {
    try {
      const response = (await getAPI(
        `/v1/shipments/admin/${shipmentId}`,
      )) as SwayyAPIResponse<SwayyShipmentFullDetail>;

      const payload = response.data ?? response.result;
      if (!response.success || !payload?.shipment) {
        throw new Error(response.message || "Failed to fetch shipment");
      }

      return {
        shipment: {
          ...payload.shipment,
          items: payload.shipment.items ?? [],
        },
        inventory_movements: payload.inventory_movements ?? [],
        audit_trail: payload.audit_trail ?? [],
      };
    } catch (error) {
      throw new Error(getErrorMessage(error, "Failed to fetch shipment"));
    }
  };

  getOrderShipments = async (
    orderId: number | string,
  ): Promise<SwayyShipment[]> => {
    try {
      const response = (await getAPI(
        `/v1/orders/admin/${orderId}/shipments`,
      )) as SwayyAPIResponse<{ shipments: SwayyShipment[] }>;

      const payload = response.data ?? response.result;
      if (!response.success || !payload) {
        throw new Error(response.message || "Failed to fetch order shipments");
      }

      return (payload.shipments ?? []).map((shipment) => ({
        ...shipment,
        items: shipment.items ?? [],
      }));
    } catch (error) {
      throw new Error(getErrorMessage(error, "Failed to fetch order shipments"));
    }
  };

  createOrderShipment = async (
    orderId: number | string,
    body: CreateSwayyShipmentPayload,
  ): Promise<SwayyShipment> => {
    try {
      const response = (await postAPI(
        `/v1/orders/admin/${orderId}/shipments`,
        body,
      )) as SwayyAPIResponse<{ shipment: SwayyShipment } | SwayyShipment>;

      const payload = response.data ?? response.result;
      if (!response.success || !payload) {
        throw new Error(response.message || "Failed to create shipment");
      }

      if ("shipment" in payload && payload.shipment) {
        return {
          ...payload.shipment,
          items: payload.shipment.items ?? [],
        };
      }

      return {
        ...(payload as SwayyShipment),
        items: (payload as SwayyShipment).items ?? [],
      };
    } catch (error) {
      throw new Error(getErrorMessage(error, "Failed to create shipment"));
    }
  };

  updateShipment = async (
    shipmentId: number | string,
    body: UpdateSwayyShipmentPayload,
  ): Promise<SwayyShipment> => {
    try {
      const response = (await patchAPI(
        `/v1/shipments/admin/${shipmentId}`,
        body,
      )) as SwayyAPIResponse<{ shipment: SwayyShipment } | SwayyShipment>;

      const payload = response.data ?? response.result;
      if (!response.success || !payload) {
        throw new Error(response.message || "Failed to update shipment");
      }

      if ("shipment" in payload && payload.shipment) {
        return {
          ...payload.shipment,
          items: payload.shipment.items ?? [],
        };
      }

      return {
        ...(payload as SwayyShipment),
        items: (payload as SwayyShipment).items ?? [],
      };
    } catch (error) {
      throw new Error(getErrorMessage(error, "Failed to update shipment"));
    }
  };

  downloadCourierCsv = async (
    courier: SwayyCourierPartner,
  ): Promise<{ blob: Blob; filename: string }> => {
    try {
      const { blob, filename } = await postFileAPI(
        "/v1/shipments/admin/courier-csv",
        { courier },
      );
      const fallback =
        courier === "shipmozo"
          ? "shipmozo_template.xlsx"
          : "shiprocket_template.csv";
      return {
        blob,
        filename: filename && filename !== "export.csv" ? filename : fallback,
      };
    } catch (error) {
      throw new Error(
        getErrorMessage(error, `Failed to download ${courier} template`),
      );
    }
  };

  deleteShipment = async (shipmentId: number | string): Promise<void> => {
    try {
      const response = (await deleteAPI(
        `/v1/shipments/admin/${shipmentId}`,
      )) as SwayyAPIResponse<null>;

      if (!response.success) {
        throw new Error(response.message || "Failed to delete shipment");
      }
    } catch (error) {
      throw new Error(getErrorMessage(error, "Failed to delete shipment"));
    }
  };
}
